import React, { useState } from "react";
import { Auth } from "aws-amplify";
import { Dialog } from "primereact/dialog";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import "./change_password.css";

// Self-service password change for the signed-in Cognito user (opened from
// NavigationBar.js's account menu). Cognito itself enforces the pool's
// password policy and checks the current password - this form only does
// the obvious client-side checks first, then shows Cognito's own message
// for anything it rejects. The existing session/token stays valid, so the
// user isn't signed out afterwards.
const EMPTY = { current: "", next: "", confirm: "" };

// Cognito's messages for these are terse/technical ("Incorrect username or
// password.") - reworded for this form's context. Anything else (notably
// InvalidPasswordException, which spells out the violated policy rule) is
// shown as-is.
const friendly_error = (err) => {
  // Amplify rejects with a plain string for some client-side failures
  // (e.g. "The user is not authenticated").
  if (typeof err === "string") {
    return err;
  }
  switch (err?.code || err?.name) {
    case "NotAuthorizedException":
      return "Your current password is incorrect.";
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts. Please wait a few minutes and try again.";
    default:
      return err?.message || "Your password could not be changed. Please try again.";
  }
};

const ChangePasswordDialog = ({ visible, onHide, onChanged }) => {
  const [values, setValues] = useState(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set_field = (name) => (e) => setValues({ ...values, [name]: e.target.value });

  const field_errors = {
    current: !values.current ? "Enter your current password." : "",
    next: !values.next
      ? "Enter a new password."
      : values.next === values.current
        ? "The new password must be different from the current one."
        : "",
    confirm: values.confirm !== values.next ? "Passwords do not match." : "",
  };
  const is_valid = !field_errors.current && !field_errors.next && !field_errors.confirm;

  const close = () => {
    setValues(EMPTY);
    setSubmitted(false);
    setError("");
    onHide();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setError("");
    if (!is_valid) {
      return;
    }
    setSaving(true);
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, values.current, values.next);
      close();
      onChanged && onChanged();
    } catch (err) {
      setError(friendly_error(err));
    } finally {
      setSaving(false);
    }
  };

  const render_field = (name, label, autoComplete, hint) => {
    const show_error = submitted && field_errors[name];
    return (
      <div className="cp-field">
        <label htmlFor={`cp-${name}`} className="cp-label">{label}</label>
        <Password
          inputId={`cp-${name}`}
          value={values[name]}
          onChange={set_field(name)}
          feedback={false}
          toggleMask
          autoComplete={autoComplete}
          className={show_error ? "p-invalid" : ""}
          inputClassName="cp-input"
        />
        {show_error
          ? <small className="p-error">{field_errors[name]}</small>
          : hint && <small className="cp-hint">{hint}</small>}
      </div>
    );
  };

  const footer = (
    <div className="cp-footer">
      <Button type="button" label="Cancel" className="p-button-text" onClick={close} disabled={saving} />
      <Button type="submit" form="change-password-form" label="Change password" icon="pi pi-check" loading={saving} />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={close}
      header={
        <div className="cp-header">
          <span className="cp-header-icon"><i className="pi pi-key" /></span>
          <div>
            <div className="cp-header-title">Change password</div>
            <div className="cp-header-subtitle">You'll stay signed in on this device.</div>
          </div>
        </div>
      }
      footer={footer}
      style={{ width: "440px" }}
      breakpoints={{ "575px": "calc(100vw - 32px)" }}
      modal
      closable={!saving}
      className="cp-dialog"
    >
      <form id="change-password-form" onSubmit={submit} className="cp-form" noValidate>
        {error && <div className="ex-inline-error">{error}</div>}
        {render_field("current", "Current password", "current-password")}
        {render_field("next", "New password", "new-password", "Must meet your organisation's password policy.")}
        {render_field("confirm", "Confirm new password", "new-password")}
      </form>
    </Dialog>
  );
};

export default ChangePasswordDialog;
